import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

interface ModeIndicatorProps {
  isTopDown: boolean;
  isBottomUp: boolean;
  onToggleTopDown: () => void;
  viewType?: string;
}

const ModeIndicator: React.FC<ModeIndicatorProps> = ({
  isTopDown,
  isBottomUp,
  onToggleTopDown,
  viewType,
}) => {
  const backgroundColor = isTopDown
    ? '#FFF3E0'
    : isBottomUp
      ? '#E3F2FD'
      : '#F5F5F5';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        padding: '6px 16px',
        backgroundColor,
        borderBottom: '1px solid #E0E0E0',
        fontSize: 12,
      }}
    >
      {/* Top-Down toggle button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          component="button"
          onClick={onToggleTopDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: isTopDown ? '#FF9800' : '#E0E0E0',
            color: isTopDown ? '#FFF' : '#888',
            borderRadius: '12px',
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            lineHeight: 1.5,
            '&:hover': {
              opacity: 0.85,
            },
          }}
        >
          {isTopDown ? (
            <ToggleOnIcon sx={{ fontSize: 16 }} />
          ) : (
            <ToggleOffIcon sx={{ fontSize: 16 }} />
          )}
          Top-Down
        </Box>
        {isTopDown && (
          <Typography
            component="span"
            sx={{ fontSize: 11, color: '#E65100', ml: 0.5 }}
          >
            TD-Only columns visible
          </Typography>
        )}
      </Box>

      {/* Vertical divider */}
      <Box
        sx={{
          width: '1px',
          height: '16px',
          backgroundColor: '#DDD',
        }}
      />

      {/* Bottom-Up indicator (read-only) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: isBottomUp ? '#1976D2' : '#E0E0E0',
            color: isBottomUp ? '#FFF' : '#AAA',
            borderRadius: '12px',
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          Bottom-Up
        </Box>
        <Typography component="span" sx={{ fontSize: 10, color: '#999' }}>
          (read-only)
        </Typography>
      </Box>

      {/* Right side spacer + viewType badge */}
      <Box sx={{ flex: 1 }} />
      {viewType && (
        <Box
          sx={{
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: '10px',
            backgroundColor: '#E8F5E9',
            color: '#2E7D32',
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          {viewType}
        </Box>
      )}
    </Box>
  );
};

export default ModeIndicator;
